import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Globe, ChevronDown, ChevronRight, Loader, Shield, AlertTriangle,
  Key, Activity, TrendingUp, TrendingDown, Lock, Zap, Eye,
  RefreshCw, AlertCircle, Database, Target, BarChart2, Minus,
  CheckCircle, Copy, Check, ExternalLink
} from "lucide-react";
import Navigation from "../components/Navigation";
import Card from "../components/Card";
import { useApi } from "../hooks/useApi";
import { useModuleAvailability } from "../hooks/useModuleAvailability";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, AreaChart, Area, RadialBarChart, RadialBar
} from "recharts";

// ─── Risk Color Palette (matches app: red / orange / yellow / green) ──────────

const RISK_COLORS = {
  critical: { bg: "bg-red-600",    text: "text-red-600",    light: "bg-red-50",    border: "border-red-200",    hex: "#dc2626" },
  high:     { bg: "bg-red-500",    text: "text-red-500",    light: "bg-red-50",    border: "border-red-200",    hex: "#ef4444" },
  medium:   { bg: "bg-orange-500", text: "text-orange-500", light: "bg-orange-50", border: "border-orange-200", hex: "#f97316" },
  low:      { bg: "bg-yellow-500", text: "text-yellow-600", light: "bg-yellow-50", border: "border-yellow-200", hex: "#eab308" },
  safe:     { bg: "bg-green-500",  text: "text-green-600",  light: "bg-green-50",  border: "border-green-200",  hex: "#22c55e" },
};

const getRiskLevel = (total) => {
  if (total >= 100) return { level: "Critical", ...RISK_COLORS.critical, badgeCls: "bg-red-600 text-white" };
  if (total >= 50)  return { level: "High",     ...RISK_COLORS.high,     badgeCls: "bg-red-500 text-white" };
  if (total >= 20)  return { level: "Medium",   ...RISK_COLORS.medium,   badgeCls: "bg-orange-500 text-white" };
  if (total > 0)    return { level: "Low",      ...RISK_COLORS.low,      badgeCls: "bg-yellow-500 text-white" };
  return               { level: "None",      ...RISK_COLORS.safe,     badgeCls: "bg-gray-400 text-white" };
};

// Color a bar by exposure count
const getRiskBarHex = (count) => {
  if (count >= 100) return RISK_COLORS.critical.hex;
  if (count >= 50)  return RISK_COLORS.high.hex;
  if (count >= 20)  return RISK_COLORS.medium.hex;
  if (count >= 5)   return RISK_COLORS.low.hex;
  return RISK_COLORS.safe.hex;
};

// Color a category by name
const getCategoryHex = (category) => {
  const c = (category || "").toLowerCase();
  if (c.includes("breach") || c.includes("malware") || c.includes("stealer")) return RISK_COLORS.critical.hex;
  if (c.includes("credential") || c.includes("phish") || c.includes("combo"))  return RISK_COLORS.high.hex;
  if (c.includes("password") || c.includes("leak"))                             return RISK_COLORS.medium.hex;
  if (c.includes("email") || c.includes("forum"))                               return RISK_COLORS.low.hex;
  if (c.includes("historical"))                                                 return "#d97706"; // amber, matches the "Historical" row badge
  return "#6366f1";
};

// Classify password type risk
const classifyPasswordRisk = (type) => {
  const t = (type || "").toLowerCase();
  if (t.includes("plain") || t.includes("clear") || t.includes("unknown") || !type) return "Plaintext (Critical)";
  if (t.includes("md5") || t.includes("sha1"))   return "Weak Hash (High)";
  if (t.includes("sha2") || t.includes("sha-2")) return "Moderate Hash";
  if (t.includes("bcrypt") || t.includes("argon") || t.includes("scrypt")) return "Strong Hash (Safe)";
  return "Unknown";
};

const passwordRiskHex = (label) => {
  if (label.includes("Critical")) return RISK_COLORS.critical.hex;
  if (label.includes("High"))     return RISK_COLORS.high.hex;
  if (label.includes("Moderate")) return RISK_COLORS.medium.hex;
  if (label.includes("Safe"))     return RISK_COLORS.safe.hex;
  return "#94a3b8";
};

// ─── General Helpers ──────────────────────────────────────────────────────────

const getExposurePattern = (n) => {
  if (n >= 3) return "Clustered activity detected";
  if (n === 2) return "Multi-vector activity detected";
  if (n === 1) return "Isolated activity detected";
  return "No activity detected";
};

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return "N/A";
  const diffDays = Math.floor((Date.now() - new Date(dateStr)) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 30)  return `${diffDays}d ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}yr ago`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

// Polled (monitoring-only) exposures have no real exposure date — only the poll-window start.
// We surface a coarse "Last N days" window derived from that start instead of an exact date.
const formatExposureWindow = (startDateStr) => {
  if (!startDateStr) return "Last 90 days";
  const diffDays = Math.floor((Date.now() - new Date(startDateStr)) / 86400000);
  if (diffDays <= 2)  return "Last 2 days";
  if (diffDays <= 7)  return "Last 7 days";
  if (diffDays <= 30) return "Last 30 days";
  return "Last 90 days";
};

// A row is "monitoring-only" (polled, email-only) when it carries a poll-window StartDate but no
// real ExposedAt. Webhook-sourced rows have ExposedAt and full breach detail.
const isMonitoringOnlyExposure = (detail) => !detail.exposedAt && !!detail.startDate;

function getTelegramRiskDescription(signal) {
  const types = new Set((signal.ioCs ?? []).map(i => i.type));
  if (types.has("email"))  return "Credential exposure detected. Corporate email addresses found in threat actor communications indicate a possible credential dump or phishing operation targeting your organisation.";
  if (types.has("cve"))    return "Active exploitation chatter. CVE identifiers in dark-web channels indicate threat actors are actively discussing or targeting known vulnerabilities.";
  if (types.has("btc"))    return "Ransomware or extortion indicators. Bitcoin addresses suggest financial threat transactions linked to criminal activity.";
  if (types.has("domain")) return "Suspicious infrastructure identified. Domain indicators may represent phishing, C2, or data exfiltration infrastructure.";
  if (types.has("ip"))     return "Malicious IP activity. IP addresses mentioned suggest scanning, C2 communication, or attack infrastructure.";
  if (signal.severity === "Critical" || signal.severity === "High")
    return "High-confidence dark-web threat signal matching your monitored keywords. Immediate investigation recommended.";
  return "Dark-web channel activity matching configured keyword filters. Monitor for escalation.";
}

function getTelegramRemediationSteps(signal) {
  const types = new Set((signal.ioCs ?? []).map(i => i.type));
  const steps = [];
  if (types.has("email"))  steps.push("Force password reset for all identified email accounts", "Enforce MFA on affected accounts", "Review login history for anomalous access");
  if (types.has("cve"))    steps.push("Identify and emergency-patch affected systems", "Hunt for exploitation indicators in SIEM/EDR logs", "Deploy detection rules for the identified CVE");
  if (types.has("btc"))    steps.push("Cross-reference BTC address with threat intel feeds", "Preserve evidence chain for potential law enforcement engagement");
  if (types.has("domain")) steps.push("Block identified domains at email gateway and firewall", "Submit domain for takedown if phishing confirmed", "Search email logs for user clicks on identified domains");
  if (types.has("ip"))     steps.push("Block identified IPs at perimeter firewall", "Review network flow logs for connections to identified IPs in past 72 hours");
  if (steps.length === 0)  steps.push("Review message context in full", "Assess relevance and exposure to your organisation", "Escalate to IR if pattern intensifies");
  return steps;
}

const getCategoryBadge = (category) => {
  const c = (category || "").toLowerCase();
  if (c.includes("breach"))      return "bg-red-100 text-red-700";
  if (c.includes("credential"))  return "bg-orange-100 text-orange-700";
  if (c.includes("password"))    return "bg-amber-100 text-amber-700";
  if (c.includes("email"))       return "bg-blue-100 text-blue-700";
  if (c.includes("phish"))       return "bg-purple-100 text-purple-700";
  if (c.includes("malware"))     return "bg-red-100 text-red-800";
  if (c.includes("combo"))       return "bg-pink-100 text-pink-700";
  return "bg-gray-100 text-gray-700";
};

const getPasswordTypeBadge = (type) => {
  const t = (type || "").toLowerCase();
  if (t.includes("plain") || t.includes("clear") || !type) return "bg-red-100 text-red-700";
  if (t.includes("md5") || t.includes("sha1"))  return "bg-orange-100 text-orange-700";
  if (t.includes("sha2"))                        return "bg-yellow-100 text-yellow-700";
  if (t.includes("bcrypt") || t.includes("argon")) return "bg-green-100 text-green-700";
  return "bg-gray-100 text-gray-600";
};

// ─── Intelligence Banner + KPI (two-column) ───────────────────────────────────

const IntelligenceBanner = ({ totalExposures, topDomain, categoryData, timelineData, uniqueDomains, allDetails }) => {
  const risk = getRiskLevel(totalExposures);
  const pattern = getExposurePattern(categoryData?.length || 0);
  const detectionWindow = timelineData?.day30 > 0
    ? `Last 30 days (${timelineData.day30} new)`
    : timelineData?.day60 > 0 ? "Last 60 days" : "Last 90 days";

  // KPI calculations
  const day30 = timelineData?.day30 || 0;
  const day60 = timelineData?.day60 || 0;
  const prior30 = day60 - day30;
  const velocityPct = prior30 > 0 ? Math.round(((day30 - prior30) / prior30) * 100) : 0;
  const velocityUp = day30 >= prior30;
  const totalCredentials = allDetails.filter(d => {
    const t = (d.passwordType || "").toLowerCase();
    return t && t !== "none" && t !== "unknown";
  }).length || allDetails.length;
  const mostRecentDate = allDetails.reduce((latest, d) => {
    if (!d.exposedAt) return latest;
    const dt = new Date(d.exposedAt);
    return (!latest || dt > latest) ? dt : latest;
  }, null);
  const daysSinceLast = mostRecentDate ? Math.floor((Date.now() - mostRecentDate) / 86400000) : null;

  const snapshots = [
    { label: "Domain",            value: topDomain || "N/A" },
    { label: "Risk Level",        value: <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${risk.badgeCls}`}>{risk.level}</span> },
    { label: "Exposure Pattern",  value: pattern },
    { label: "Detection Window",  value: detectionWindow },
    { label: "Total Exposures",   value: <span className={`font-bold ${risk.text}`}>{totalExposures}</span> },
    { label: "Impacted Accounts", value: (() => {
        const unique = new Set(allDetails.map(d => d.username).filter(Boolean)).size;
        if (unique > 0) return `${unique} unique account${unique !== 1 ? "s" : ""}`;
        if (totalExposures > 0) return `${totalExposures} exposed record${totalExposures !== 1 ? "s" : ""}`;
        return "None detected";
      })() },
  ];

  const kpis = [
    {
      icon: <Eye className="h-4 w-4 text-red-400" />,
      label: "Total Exposures",
      value: totalExposures,
      sub: "Last 90 days",
      valueClass: risk.text,
      bg: "bg-red-50", border: "border-red-100",
    },
    {
      icon: velocityUp ? <TrendingUp className="h-4 w-4 text-orange-400" /> : <TrendingDown className="h-4 w-4 text-green-400" />,
      label: "Threat Velocity",
      value: day30 > 0 ? `${day30} / 30d` : "—",
      sub: prior30 > 0 ? `${velocityUp ? "▲" : "▼"} ${Math.abs(velocityPct)}% vs prior 30d` : "First 30d window",
      valueClass: velocityUp ? "text-orange-500" : "text-green-600",
      bg: velocityUp ? "bg-orange-50" : "bg-green-50",
      border: velocityUp ? "border-orange-100" : "border-green-100",
    },
    {
      icon: <Globe className="h-4 w-4 text-indigo-400" />,
      label: "Monitored Domains",
      value: uniqueDomains,
      sub: uniqueDomains > 1 ? "Multiple attack surfaces" : "Single domain",
      valueClass: "text-indigo-600",
      bg: "bg-indigo-50", border: "border-indigo-100",
    },
    {
      icon: <Database className="h-4 w-4 text-purple-400" />,
      label: "Credentials at Risk",
      value: totalCredentials > 0 ? formatCompact(totalCredentials) : "—",
      sub: daysSinceLast !== null ? `Last detected ${daysSinceLast === 0 ? "today" : `${daysSinceLast}d ago`}` : "Expand a domain",
      valueClass: totalCredentials >= 1_000_000_000 ? "text-red-600" : totalCredentials >= 1_000_000 ? "text-orange-500" : "text-purple-600",
      bg: totalCredentials >= 1_000_000_000 ? "bg-red-50" : "bg-purple-50",
      border: totalCredentials >= 1_000_000_000 ? "border-red-100" : "border-purple-100",
    },
  ];

  if (totalExposures === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">

      {/* Left — Alert + Snapshot */}
      <div className="bg-white rounded-xl border border-red-200 shadow-sm overflow-hidden transition-all duration-200 ease-out hover:-translate-y-1 hover:shadow-md">
        <div className="h-1" style={{ background: `linear-gradient(to right, ${RISK_COLORS.critical.hex}, ${RISK_COLORS.medium.hex})` }} />
        <div className="p-5">
          {/* Title row */}
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <div className="w-8 h-8 rounded-lg bg-red-100 border border-red-200 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="h-4 w-4 text-red-500" />
            </div>
            <span className="text-gray-900 font-bold text-sm">Axaris Intelligence Alert</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${risk.badgeCls}`}>{risk.level} Risk</span>
            <span className="flex items-center gap-1 text-xs text-gray-400 ml-auto">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse inline-block" />
              Live
            </span>
          </div>
          {/* Description */}
          <p className="text-sm text-gray-600 leading-relaxed mb-4">
            Axaris has identified{" "}
            <span className="font-bold text-gray-900">{totalExposures} dark web exposure signal{totalExposures !== 1 ? "s" : ""}</span>
            {topDomain && <> associated with <span className="font-bold text-indigo-600">{topDomain}</span></>}{" "}
            that indicate an elevated likelihood of unauthorized access.
          </p>
          {/* Snapshot fields — 2 columns */}
          <div className="border-t border-gray-100 pt-4 grid grid-cols-2 gap-x-6 gap-y-3">
            {snapshots.map(({ label, value }) => (
              <div key={label}>
                <p className="text-xs text-gray-400 mb-0.5">{label}</p>
                <p className="text-sm font-semibold text-gray-800">{value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right — KPI 2×2 grid inside white card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden transition-all duration-200 ease-out hover:-translate-y-1 hover:shadow-md">
        <div className="h-1" style={{ background: `linear-gradient(to right, #6366f1, #8b5cf6)` }} />
        <div className="p-5 grid grid-cols-2 gap-4 h-full">
          {kpis.map(({ icon, label, value, sub, valueClass, bg, border }) => (
            <div key={label} className={`${bg} ${border} border rounded-xl p-4 flex flex-col justify-between`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-gray-500 font-medium">{label}</p>
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${bg}`}>{icon}</div>
              </div>
              <p className={`text-2xl font-bold ${valueClass}`}>{value}</p>
              {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};

// ─── Why This Matters Card ────────────────────────────────────────────────────

const WhyThisMattersCard = ({ categoryData, totalExposures }) => {
  const hasClustered = (categoryData?.length || 0) >= 3;
  const hasPasswordData = categoryData?.some(c => {
    const cat = (c.category || "").toLowerCase();
    return cat.includes("password") || cat.includes("credential");
  });
  const hasHighVolume = totalExposures >= 50;
  const topCategories = (categoryData || []).slice(0, 2).map(c => c.category).filter(Boolean);
  const attackVector = topCategories.length > 0
    ? topCategories.join(" and ").toLowerCase()
    : "unauthorized access";

  const bullets = [
    { icon: <Lock className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />,
      text: `${totalExposures > 0 ? totalExposures : "Detected"} credential exposure${totalExposures !== 1 ? "s" : ""} on the dark web — frequently leveraged in automated attack attempts.` },
    ...(hasClustered ? [{ icon: <Activity className="h-4 w-4 text-orange-500 flex-shrink-0 mt-0.5" />,
      text: `Activity spans ${categoryData.length} categories (${categoryData.slice(0,3).map(c=>c.category).join(", ")}) — elevated attack surface across multiple systems.` }] : []),
    { icon: <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />,
      text: `Increased probability of ${attackVector} attacks targeting email and cloud platforms.` },
    ...(hasPasswordData ? [{ icon: <Key className="h-4 w-4 text-purple-500 flex-shrink-0 mt-0.5" />,
      text: "Password/credential category detected — privilege escalation risk if credentials are reused across systems." }] : []),
    ...(hasHighVolume ? [{ icon: <TrendingUp className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />,
      text: `${totalExposures}+ exposures detected — elevated risk of automated credential-stuffing attacks.` }] : []),
  ];

  return (
    <Card>
      <div className="flex items-center gap-2 mb-4">
        <AlertTriangle className="h-4 w-4 text-amber-500" />
        <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">Why This Matters</h3>
      </div>
      <div className="space-y-3">
        {bullets.map((b, i) => (
          <div key={i} className="flex items-start gap-2.5">
            {b.icon}
            <p className="text-sm text-gray-600 leading-snug">{b.text}</p>
          </div>
        ))}
      </div>
    </Card>
  );
};

// ─── Immediate Actions Card ───────────────────────────────────────────────────

const ImmediateActionsCard = ({ fullWidth, allDetails, timelineData, totalExposures }) => {
  const hasPlaintext = useMemo(() =>
    (allDetails || []).some(d => d.password && !d.password.startsWith("$") && d.password.length < 64),
  [allDetails]);

  const isAccelerating = useMemo(() => {
    const tl = Array.isArray(timelineData) ? timelineData : [];
    const sorted = [...tl].sort((a, b) => new Date(a.date) - new Date(b.date));
    const day30 = sorted.slice(-30).reduce((s, p) => s + (p.count || 0), 0);
    const prior30 = sorted.slice(-60, -30).reduce((s, p) => s + (p.count || 0), 0);
    return prior30 > 0 && day30 > prior30;
  }, [timelineData]);

  const hasMultipleSources = useMemo(() => {
    const sources = new Set((allDetails || []).map(d => d.source).filter(Boolean));
    return sources.size >= 2;
  }, [allDetails]);

  const actions = [
    { icon: <Key className="h-4 w-4 text-red-500" />, text: "Reset credentials for all impacted accounts", priority: true },
    ...(hasPlaintext ? [{ icon: <Shield className="h-4 w-4 text-indigo-500" />, text: "Enforce MFA — plaintext credentials detected in breach data" }] : [
      { icon: <Shield className="h-4 w-4 text-indigo-500" />, text: "Validate MFA coverage across all user accounts" },
    ]),
    ...(isAccelerating ? [{ icon: <RefreshCw className="h-4 w-4 text-amber-500" />, text: "Revoke active sessions — accelerating breach activity detected" }] : []),
    { icon: <Eye className="h-4 w-4 text-blue-500" />, text: "Review recent authentication and access logs for anomalies" },
    ...(hasMultipleSources ? [{ icon: <Lock className="h-4 w-4 text-purple-500" />, text: "Audit credential reuse — exposures span multiple breach sources" }] : []),
    ...(!isAccelerating && !hasMultipleSources ? [{ icon: <Lock className="h-4 w-4 text-purple-500" />, text: "Audit credential reuse across critical systems" }] : []),
  ];
  return (
    <Card>
      <div className="flex items-center gap-2 mb-4">
        <Zap className="h-4 w-4 text-yellow-500" />
        <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">Immediate Actions</h3>
      </div>
      <div className={fullWidth ? "grid grid-cols-2 md:grid-cols-5 gap-3" : "space-y-3"}>
        {actions.map((action, i) => (
          <div key={i} className={fullWidth
            ? "flex flex-col gap-2 p-3 rounded-lg bg-gray-50 border border-gray-100"
            : "flex items-start gap-3"}>
            <div className={fullWidth ? "flex items-center gap-2" : "flex items-start gap-3 flex-1"}>
              <div className="flex-shrink-0">{action.icon}</div>
              <p className="text-sm text-gray-700 leading-snug">{action.text}</p>
            </div>
            {action.priority && <span className="self-start px-1.5 py-0.5 bg-red-100 text-red-600 text-xs font-semibold rounded">Priority</span>}
          </div>
        ))}
      </div>
    </Card>
  );
};

// ─── Exposures by Domain — Horizontal Bar Chart ───────────────────────────────

const ExposuresByDomainChart = ({ data, loading, monitoredCount }) => {
  if (loading) return <Card><div className="h-52 flex items-center justify-center text-sm text-gray-400"><Loader className="h-5 w-5 animate-spin mr-2 text-red-400" />Loading...</div></Card>;
  const items = (data || []).slice(0, 8);
  const total = items.reduce((s, i) => s + i.count, 0);

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">Exposures by Domain</h3>
          <p className="text-xs text-gray-400 mt-0.5">Last 90 days — color = risk level</p>
        </div>
        <span className="px-2.5 py-1 bg-red-50 text-red-700 text-xs font-bold rounded-lg border border-red-100">{total} total</span>
      </div>
      {items.length === 0
        ? <div className="h-40 flex flex-col items-center justify-center text-sm text-gray-400">
            <span>No exposures found</span>
            {monitoredCount > 0 && <span className="text-xs text-green-600 mt-1">{monitoredCount} domain{monitoredCount !== 1 ? "s" : ""} monitored</span>}
          </div>
        : (
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={items} layout="vertical" margin={{ top: 0, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f3f4f6" />
              <XAxis type="number" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis type="category" dataKey="domain" tick={{ fontSize: 10 }} width={90} tickLine={false} axisLine={false} />
              <Tooltip formatter={(val) => [val, "Exposures"]} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }} />
              <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={14}>
                {items.map((entry, i) => <Cell key={i} fill={getRiskBarHex(entry.count)} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      {/* Risk color legend */}
      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
        {[["≥100", RISK_COLORS.critical.hex, "Critical"], ["≥50", RISK_COLORS.high.hex, "High"], ["≥20", RISK_COLORS.medium.hex, "Medium"], ["≥5", RISK_COLORS.low.hex, "Low"], ["<5", RISK_COLORS.safe.hex, "Safe"]].map(([range, hex, label]) => (
          <span key={label} className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: hex }} />{range} {label}</span>
        ))}
      </div>
    </Card>
  );
};

// ─── Exposures by Category — Pie Chart ───────────────────────────────────────

const RADIAN = Math.PI / 180;
const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.07) return null;
  const r = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" style={{ fontSize: 10, fontWeight: 600 }}>{`${(percent * 100).toFixed(0)}%`}</text>;
};

const ExposuresByCategoryChart = ({ data, loading }) => {
  if (loading) return <Card><div className="h-52 flex items-center justify-center text-sm text-gray-400"><Loader className="h-5 w-5 animate-spin mr-2 text-orange-400" />Loading...</div></Card>;
  const items = data || [];
  const total = items.reduce((s, i) => s + i.count, 0);

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">Exposures by Category</h3>
          <p className="text-xs text-gray-400 mt-0.5">Last 90 days — color = threat type</p>
        </div>
        <span className="px-2.5 py-1 bg-orange-50 text-orange-700 text-xs font-bold rounded-lg border border-orange-100">{total} total</span>
      </div>
      {items.length === 0
        ? <div className="h-40 flex items-center justify-center text-sm text-gray-400">No exposures found</div>
        : (
          <>
            <ResponsiveContainer width="100%" height={155}>
              <PieChart>
                <Pie data={items} dataKey="count" nameKey="category" cx="50%" cy="50%" outerRadius={68} labelLine={false} label={renderPieLabel}>
                  {items.map((entry, i) => <Cell key={i} fill={getCategoryHex(entry.category)} />)}
                </Pie>
                <Tooltip formatter={(val, name) => [val, name]} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-2 mt-2">
              {items.map((item) => (
                <div key={item.category} className="flex items-center gap-1">
                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: getCategoryHex(item.category) }} />
                  <span className="text-xs text-gray-600 truncate max-w-[90px]" title={item.category}>{item.category}</span>
                </div>
              ))}
            </div>
          </>
        )}
    </Card>
  );
};

// ─── Exposure Timeline — Area Chart ──────────────────────────────────────────

const ExposureTimelineChart = ({ data, loading }) => {
  if (loading) return <Card><div className="h-52 flex items-center justify-center text-sm text-gray-400"><Loader className="h-5 w-5 animate-spin mr-2 text-yellow-400" />Loading...</div></Card>;
  const { day30 = 0, day60 = 0, day90 = 0 } = data || {};
  const prior30 = day60 - day30;
  const isIncreasing = day30 > prior30 && day30 > 0;
  const areaColor = isIncreasing ? "#ef4444" : "#22c55e";
  const chartData = [
    { period: "90d", count: day90 },
    { period: "60d", count: day60 },
    { period: "30d", count: day30 },
  ];

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">Exposure Timeline</h3>
          <p className="text-xs text-gray-400 mt-0.5">Cumulative detection windows</p>
        </div>
        <span className={`flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-semibold border ${isIncreasing ? "bg-red-50 text-red-600 border-red-100" : "bg-green-50 text-green-600 border-green-100"}`}>
          {isIncreasing ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
          {isIncreasing ? "Accelerating" : "Stable"}
        </span>
      </div>
      <ResponsiveContainer width="100%" height={155}>
        <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id="timelineGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={areaColor} stopOpacity={0.18} />
              <stop offset="95%" stopColor={areaColor} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis dataKey="period" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
          <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }} />
          <Area type="monotone" dataKey="count" stroke={areaColor} strokeWidth={2.5} fill="url(#timelineGrad)" dot={{ r: 5, fill: areaColor, strokeWidth: 0 }} name="Exposures" />
        </AreaChart>
      </ResponsiveContainer>
      <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-gray-100">
        {[{ label: "30 Days", val: day30, color: "text-red-600" }, { label: "60 Days", val: day60, color: "text-orange-500" }, { label: "90 Days", val: day90, color: "text-yellow-600" }].map(({ label, val, color }) => (
          <div key={label} className="text-center">
            <div className={`text-xl font-bold ${color}`}>{val}</div>
            <div className="text-xs text-gray-400">{label}</div>
          </div>
        ))}
      </div>
    </Card>
  );
};

// ─── Intelligence Insights Panel ─────────────────────────────────────────────

const PasswordSecurityProfile = ({ allDetails }) => {
  const counts = useMemo(() => {
    const map = {};
    allDetails.forEach(d => {
      const label = classifyPasswordRisk(d.passwordType);
      map[label] = (map[label] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [allDetails]);

  const total = counts.reduce((s, [, v]) => s + v, 0);
  const plaintextPct = total > 0
    ? Math.round(((counts.find(([k]) => k.includes("Critical"))?.[1] || 0) / total) * 100)
    : 0;

  if (allDetails.length === 0) return (
    <Card>
      <div className="flex items-center gap-2 mb-2">
        <Key className="h-4 w-4 text-orange-500" />
        <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">Password Security Profile</h3>
      </div>
      <p className="text-xs text-gray-400 italic py-3 text-center">Expand a domain in the table below to load credential details.</p>
    </Card>
  );

  return (
    <Card>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Key className="h-4 w-4 text-orange-500" />
          <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">Password Security Profile</h3>
        </div>
        <div className="flex items-center gap-2">
          {plaintextPct > 0 && (
            <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded border border-red-200">
              {plaintextPct}% Plaintext
            </span>
          )}
          <span className="text-xs text-gray-400">{total} records</span>
        </div>
      </div>

      {/* Full-width stacked bar */}
      <div className="w-full h-6 rounded-lg overflow-hidden flex mb-3">
        {counts.map(([label, count]) => {
          const pct = (count / total) * 100;
          const hex = passwordRiskHex(label);
          return (
            <div
              key={label}
              title={`${label}: ${count} (${Math.round(pct)}%)`}
              style={{ width: `${pct}%`, backgroundColor: hex }}
              className="transition-all duration-500 first:rounded-l-lg last:rounded-r-lg"
            />
          );
        })}
      </div>

      {/* Legend chips in one row */}
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {counts.map(([label, count]) => {
          const pct = Math.round((count / total) * 100);
          const hex = passwordRiskHex(label);
          return (
            <div key={label} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: hex }} />
              <span className="text-xs text-gray-600">{label}</span>
              <span className="text-xs font-bold" style={{ color: hex }}>{pct}%</span>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

const AttackSourceIntelligence = ({ allDetails }) => {
  const topSources = useMemo(() => {
    const map = {};
    allDetails.forEach(d => {
      const src = d.source || "Unknown Source";
      map[src] = (map[src] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [allDetails]);

  const mostRecentDate = useMemo(() => {
    return allDetails.reduce((latest, d) => {
      if (!d.exposedAt) return latest;
      const dt = new Date(d.exposedAt);
      return (!latest || dt > latest) ? dt : latest;
    }, null);
  }, [allDetails]);

  if (allDetails.length === 0) return (
    <Card>
      <div className="flex items-center gap-2 mb-4">
        <Target className="h-4 w-4 text-red-500" />
        <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">Attack Source Intelligence</h3>
      </div>
      <p className="text-xs text-gray-400 italic py-4 text-center">Expand a domain above to load source data.</p>
    </Card>
  );

  const total = topSources.reduce((s, [, v]) => s + v, 0);

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4 text-red-500" />
          <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">Attack Source Intelligence</h3>
        </div>
        {mostRecentDate && (
          <span className="text-xs text-gray-400">Last: {formatRelativeTime(mostRecentDate)}</span>
        )}
      </div>
      {topSources.length === 0
        ? <p className="text-xs text-gray-400 italic py-2">No source data available.</p>
        : (
          <div className="space-y-2.5">
            {topSources.map(([source, count], i) => {
              const pct = Math.round((count / total) * 100);
              const hex = getRiskBarHex(count * 5); // scale for color impact
              return (
                <div key={source}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-700 font-medium truncate max-w-[170px]" title={source}>{source}</span>
                    <span className="font-bold ml-2 flex-shrink-0" style={{ color: hex }}>{count} hit{count !== 1 ? "s" : ""}</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-1.5">
                    <div className="h-1.5 rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: hex }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      <p className="text-xs text-gray-400 mt-3 pt-3 border-t border-gray-100">
        {topSources.length} unique breach source{topSources.length !== 1 ? "s" : ""} identified from loaded records.
      </p>
    </Card>
  );
};

const formatCompact = (n) => {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000)     return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)         return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};

const ThreatIndicators = ({ totalExposures, allDetails, categoryData, timelineData }) => {
  const day30   = timelineData?.day30 || 0;
  const day60   = timelineData?.day60 || 0;
  const prior30 = day60 - day30;

  const plaintextCount = useMemo(() =>
    allDetails.filter(d => { const t = (d.passwordType || "").toLowerCase(); return !t || t.includes("plain") || t.includes("clear"); }).length,
  [allDetails]);

  const uniqueSources = useMemo(() =>
    new Set(allDetails.map(d => d.source).filter(Boolean)).size,
  [allDetails]);

  const plaintextPct = allDetails.length > 0 ? Math.round((plaintextCount / allDetails.length) * 100) : 0;
  const velocityDelta = day30 - prior30;
  const isAccelerating = velocityDelta > 0 && prior30 > 0;
  const categories = categoryData?.length || 0;

  const tiles = [
    {
      label: "Plaintext Creds",
      value: allDetails.length > 0 ? `${plaintextPct}%` : "—",
      sub: plaintextPct >= 50 ? "Critical risk" : plaintextPct > 0 ? "Rotate now" : "No data",
      pct: plaintextPct,
      bg: "bg-red-50", border: "border-red-100", textColor: "text-red-600", barColor: RISK_COLORS.critical.hex,
      icon: <Key className="h-3.5 w-3.5 text-red-400" />,
    },
    {
      label: "Breach Sources",
      value: uniqueSources > 0 ? uniqueSources : "—",
      sub: uniqueSources > 2 ? "Multi-campaign" : uniqueSources > 0 ? "Identified" : "Load details",
      pct: Math.min(uniqueSources * 20, 100),
      bg: "bg-orange-50", border: "border-orange-100", textColor: "text-orange-600", barColor: RISK_COLORS.medium.hex,
      icon: <Target className="h-3.5 w-3.5 text-orange-400" />,
    },
    {
      label: "Velocity Δ (30d)",
      value: prior30 > 0 ? (velocityDelta >= 0 ? `+${velocityDelta}` : `${velocityDelta}`) : (day30 > 0 ? `${day30}` : "—"),
      sub: isAccelerating ? "Accelerating ▲" : prior30 > 0 ? "Stable ●" : "Baseline",
      pct: prior30 > 0 ? Math.min(Math.abs(velocityDelta / prior30) * 100, 100) : 50,
      bg: isAccelerating ? "bg-amber-50" : "bg-green-50",
      border: isAccelerating ? "border-amber-100" : "border-green-100",
      textColor: isAccelerating ? "text-amber-600" : "text-green-600",
      barColor: isAccelerating ? RISK_COLORS.low.hex : RISK_COLORS.safe.hex,
      icon: isAccelerating
        ? <TrendingUp className="h-3.5 w-3.5 text-amber-400" />
        : <TrendingDown className="h-3.5 w-3.5 text-green-400" />,
    },
    {
      label: "Attack Categories",
      value: categories > 0 ? categories : "—",
      sub: categories >= 3 ? "Broad surface" : categories > 0 ? "Isolated" : "No data",
      pct: Math.min(categories * 15, 100),
      bg: "bg-indigo-50", border: "border-indigo-100", textColor: "text-indigo-600", barColor: "#6366f1",
      icon: <Activity className="h-3.5 w-3.5 text-indigo-400" />,
    },
  ];

  return (
    <Card>
      <div className="flex items-center gap-2 mb-3">
        <BarChart2 className="h-4 w-4 text-indigo-500" />
        <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">Threat Indicators</h3>
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        {tiles.map(({ label, value, sub, pct, bg, border, textColor, barColor, icon }) => (
          <div key={label} className={`${bg} ${border} border rounded-lg p-2.5`}>
            <div className="flex items-center gap-1 mb-1">{icon}<span className="text-xs text-gray-500">{label}</span></div>
            <div className={`text-xl font-bold ${textColor}`}>{value}</div>
            <div className="w-full bg-white rounded-full h-1.5 mt-1.5 mb-1">
              <div className="h-1.5 rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: barColor }} />
            </div>
            <div className="text-xs text-gray-400">{sub}</div>
          </div>
        ))}
      </div>
    </Card>
  );
};

// ─── Business Risk Outlook ────────────────────────────────────────────────────

const BusinessRiskOutlook = ({ totalExposures, categoryData, timelineData }) => {
  if (totalExposures === 0) return null;
  const risk = getRiskLevel(totalExposures);

  // Derive drivers from actual category data
  const drivers = useMemo(() => {
    const categories = (categoryData || []).slice(0, 3).map(c => c.category).filter(Boolean);
    if (categories.length > 0) return categories;
    return totalExposures >= 50
      ? ["Unauthorized Access", "Data Breach", "Credential Stuffing"]
      : ["Unauthorized Access", "Data Breach"];
  }, [categoryData, totalExposures]);

  // Dynamic impact window based on threat velocity
  const impactWindow = useMemo(() => {
    const tl = Array.isArray(timelineData) ? timelineData : [];
    const sorted = [...tl].sort((a, b) => new Date(a.date) - new Date(b.date));
    const day30 = sorted.slice(-30).reduce((s, p) => s + (p.count || 0), 0);
    const prior30 = sorted.slice(-60, -30).reduce((s, p) => s + (p.count || 0), 0);
    return prior30 > 0 && day30 > prior30 ? "15–30 days" : "30–90 days";
  }, [timelineData]);

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6 shadow-sm transition-all duration-200 ease-out hover:-translate-y-1 hover:shadow-md">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <div className="w-10 h-10 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center">
            <TrendingUp className="h-5 w-5 text-amber-600" />
          </div>
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-gray-800 mb-1">Business Risk Outlook</h3>
          <p className="text-sm text-gray-600 mb-3">
            If not addressed, current exposure patterns may translate into measurable operational and financial impact over the next{" "}
            <span className="font-semibold text-gray-800">{impactWindow}</span>.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-500 font-medium">Primary risk drivers:</span>
            {drivers.map(d => (
              <span key={d} className="px-2 py-0.5 bg-slate-100 text-slate-700 text-xs font-medium rounded-full border border-slate-200">{d}</span>
            ))}
            <span className={`ml-auto px-3 py-1 rounded-lg text-xs font-bold ${risk.badgeCls}`}>
              {risk.level} · Impact window: {impactWindow}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Domain Exposures Table ───────────────────────────────────────────────────

const DomainExposuresTable = ({ domainGroups, loading, onToggle, expandedDomains, loadMore, loadingMore }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-200 ease-out hover:shadow-md">
    <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
      <div>
        <h2 className="text-base font-semibold text-gray-800">Domain Exposures</h2>
        <p className="text-xs text-gray-500 mt-0.5">Expand a domain to view exposed credential details and load intelligence data</p>
      </div>
      <Globe className="h-5 w-5 text-gray-400" />
    </div>

    {loading ? (
      <div className="flex items-center justify-center py-12">
        <Loader className="h-6 w-6 animate-spin text-red-400" />
        <span className="ml-2 text-sm text-gray-500">Loading exposures...</span>
      </div>
    ) : domainGroups.length === 0 ? (
      <div className="text-center py-14">
        <Globe className="h-10 w-10 text-gray-300 mx-auto mb-3" />
        <p className="text-sm text-gray-500">No domain exposures found.</p>
        <p className="text-xs text-gray-400 mt-1">Enroll a domain in Live Monitoring to start tracking.</p>
      </div>
    ) : (
      <div className="divide-y divide-gray-100">
        {domainGroups.map((group) => {
          const isExpanded = expandedDomains[group.domain];
          const details = group.details || [];
          const totalCount = group.totalCount || 0;
          const risk = getRiskLevel(totalCount);
          return (
            <div key={group.domain}>
              <button
                onClick={() => onToggle(group.domain)}
                className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center flex-shrink-0">
                    <Globe className="h-4 w-4 text-indigo-500" />
                  </div>
                  {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                  <span className="font-medium text-gray-800">{group.domain}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${risk.badgeCls}`}>{risk.level}</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                    {totalCount} {totalCount === 1 ? "exposure" : "exposures"}
                  </span>
                </div>
              </button>

              {isExpanded && (
                <div className="border-t border-gray-100">
                  {details.length === 0 ? (
                    <div className="px-6 py-4 text-sm text-gray-400 bg-gray-50">No exposure details available.</div>
                  ) : (
                    <>
                      <div className="overflow-x-auto">
                        <table className="min-w-full">
                          <thead>
                            <tr className="bg-slate-800">
                              <th className="px-4 py-2.5 text-left text-xs font-semibold text-white tracking-wide">Username</th>
                              <th className="px-4 py-2.5 text-left text-xs font-semibold text-white tracking-wide">Category</th>
                              <th className="px-4 py-2.5 text-left text-xs font-semibold text-white tracking-wide">Source</th>
                              <th className="px-4 py-2.5 text-left text-xs font-semibold text-white tracking-wide">Credentials</th>
                              <th className="px-4 py-2.5 text-left text-xs font-semibold text-white tracking-wide">Password Type</th>
                              <th className="px-4 py-2.5 text-left text-xs font-semibold text-white tracking-wide">Exposed</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 bg-white">
                            {details.map((detail) => {
                              const monitoringOnly = isMonitoringOnlyExposure(detail);
                              return (
                              <tr key={detail.enzoicExposureDetailsId} className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 py-2.5 text-sm font-medium text-gray-800">
                                  <div className="flex items-center gap-2">
                                    <span>{detail.username}</span>
                                    {monitoringOnly ? (
                                      <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200" title="Detected via scheduled domain monitoring — exact breach date and detail not available">Historical</span>
                                    ) : (
                                      <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-sky-50 text-sky-700 border border-sky-200" title="Pushed in real time with full breach detail">Realtime</span>
                                    )}
                                  </div>
                                </td>
                                {monitoringOnly ? (
                                  <>
                                    <td className="px-4 py-2.5 text-xs text-gray-400">—</td>
                                    <td className="px-4 py-2.5 text-xs text-gray-400">—</td>
                                    <td className="px-4 py-2.5 text-xs text-gray-400">—</td>
                                    <td className="px-4 py-2.5 text-xs text-gray-400">—</td>
                                    <td className="px-4 py-2.5"><span className="text-xs text-gray-500">{formatExposureWindow(detail.startDate)}</span></td>
                                  </>
                                ) : (
                                  <>
                                    <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded text-xs font-medium ${getCategoryBadge(detail.category)}`}>{detail.category || "N/A"}</span></td>
                                    <td className="px-4 py-2.5 text-xs text-gray-500 max-w-[120px] truncate" title={detail.source}>{detail.source || "N/A"}</td>
                                    <td className="px-4 py-2.5 text-sm font-semibold text-gray-700">{detail.numCredentials}</td>
                                    <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded text-xs font-medium ${getPasswordTypeBadge(detail.passwordType)}`}>{detail.passwordType || "Unknown"}</span></td>
                                    <td className="px-4 py-2.5"><span className="text-xs text-gray-500" title={formatDate(detail.exposedAt)}>{formatRelativeTime(detail.exposedAt)}</span></td>
                                  </>
                                )}
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between bg-gray-50">
                        <span className="text-xs text-gray-500">Showing {details.length} of {totalCount} exposures</span>
                        {group.hasMore && (
                          <button onClick={() => loadMore(group.domain)} disabled={loadingMore[group.domain]}
                            className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 disabled:opacity-50 flex items-center gap-1">
                            {loadingMore[group.domain] ? <><Loader className="h-3 w-3 animate-spin" /> Loading...</> : "Load More"}
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    )}
  </div>
);

// ─── Monitored Domains Banner ─────────────────────────────────────────────────

const MonitoredDomainsBanner = ({ subscriptions, loading, totalExposures }) => {
  if (loading || !subscriptions) return null;

  // Build grouped domains from the real API shape:
  // { organization: { primary, secondary }, deals: [...], businessUnits: [...] }
  const grouped = {};
  const org = subscriptions.organization;
  if (org) {
    const orgDomains = [];
    if (org.primaryDomain) orgDomains.push(org.primaryDomain);
    (org.secondary || []).forEach(s => { if (s.domain) orgDomains.push(s.domain); });
    if (orgDomains.length > 0) grouped["Organization"] = orgDomains;
  }
  (subscriptions.deals || []).forEach(d => {
    const domains = d.domains || (d.domain ? [{ domain: d.domain }] : []);
    domains.forEach(entry => {
      if (entry.domain) {
        if (!grouped["Deal"]) grouped["Deal"] = [];
        grouped["Deal"].push(entry.domain);
      }
    });
  });
  (subscriptions.businessUnits || []).forEach(b => {
    const domains = b.domains || (b.domain ? [{ domain: b.domain }] : []);
    domains.forEach(entry => {
      if (entry.domain) {
        if (!grouped["Business Unit"]) grouped["Business Unit"] = [];
        grouped["Business Unit"].push(entry.domain);
      }
    });
  });
  (subscriptions.vendors || []).forEach(v => {
    const domains = v.domains || (v.domain ? [{ domain: v.domain }] : []);
    domains.forEach(entry => {
      if (entry.domain) {
        if (!grouped["Vendor"]) grouped["Vendor"] = [];
        grouped["Vendor"].push(entry.domain);
      }
    });
  });

  const allDomains = Object.values(grouped).flat();
  if (allDomains.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6 overflow-hidden">
        <div className="h-1 bg-gray-200" />
        <div className="px-5 py-4">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="h-4 w-4 text-gray-400" />
            <span className="text-sm font-semibold text-gray-800">Active Monitoring</span>
          </div>
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
            <Globe className="h-5 w-5 text-gray-300 flex-shrink-0" />
            <div>
              <p className="text-sm text-gray-600 font-medium">No domains are currently being monitored</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isCompact = totalExposures > 0;

  return (
    <div className={`bg-white rounded-xl border shadow-sm mb-6 overflow-hidden transition-all duration-200 ease-out hover:-translate-y-1 hover:shadow-md ${totalExposures === 0 ? "border-green-200" : "border-gray-200"}`}>
      <div className={`h-1 ${totalExposures === 0 ? "bg-green-400" : "bg-gray-200"}`} />
      <div className={`px-5 ${isCompact ? "py-3" : "py-4"}`}>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Status indicator */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
            </span>
            <span className="text-sm font-semibold text-gray-800">Active Monitoring</span>
            <span className="px-2 py-0.5 bg-green-50 text-green-700 text-xs font-bold rounded-full border border-green-200">
              {allDomains.length} domain{allDomains.length !== 1 ? "s" : ""}
            </span>
          </div>

          {/* Domain chips */}
          <div className="flex items-center gap-2 flex-wrap flex-1">
            {Object.entries(grouped).map(([type, domainList]) => (
              <div key={type} className="flex items-center gap-1.5">
                <span className="text-xs text-gray-400 font-medium">{type}:</span>
                {domainList.map(domain => (
                  <span key={domain} className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 text-xs font-medium rounded-full border border-indigo-100">
                    <Globe className="h-3 w-3" />
                    {domain}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Reassuring message when no exposures */}
        {totalExposures === 0 && (
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-green-100">
            <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
            <p className="text-sm text-green-700">
              No exposures detected — {allDomains.length} domain{allDomains.length !== 1 ? "s" : ""} actively monitored
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const TELEGRAM_PAGE_SIZE = 20;

const DarkWebLiveMonitoringDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [domainGroups, setDomainGroups] = useState([]);
  const [expandedDomains, setExpandedDomains] = useState({});
  const [loadingMore, setLoadingMore] = useState({});
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [loadingExposures, setLoadingExposures] = useState(true);
  const [subscriptions, setSubscriptions] = useState(null);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(true);
  const [telegramSignals, setTelegramSignals]               = useState([]);
  const [loadingTelegramSignals, setLoadingTelegramSignals] = useState(true);
  const [telegramTotal, setTelegramTotal]                   = useState(0);
  const [telegramPage, setTelegramPage]                     = useState(1);
  const [telegramSeverityFilter, setTelegramSeverityFilter] = useState("");
  const [telegramLastRefresh, setTelegramLastRefresh]       = useState(null);
  const [incidents, setIncidents]                           = useState([]);
  const [loadingIncidents, setLoadingIncidents]             = useState(true);
  const [incidentTotal, setIncidentTotal]                   = useState(0);
  const [incidentPage, setIncidentPage]                     = useState(1);
  const [incidentIssueTypeFilter, setIncidentIssueTypeFilter] = useState("");
  const [incidentSeverityFilter, setIncidentSeverityFilter] = useState("");
  const [expandedIncidentIds, setExpandedIncidentIds]       = useState({});
  const [expandedSignalIds, setExpandedSignalIds]           = useState({});
  const [copiedIoC, setCopiedIoC]                           = useState(null);
  const apiCall = useApi();
  // Telegram feeds need internet access: on Restricted/AirGapped installations the Dark-Web
  // Signals and Threat Incidents panels are hidden and never fetched. Wait for the flags so nothing
  // fires before the availability map is known.
  const { loaded: flagsLoaded, isModuleAvailable } = useModuleAvailability();
  const telegramAvailable = flagsLoaded && isModuleAvailable("telegramMonitoring");

  useEffect(() => {
    apiCall("POST", "/enzoic/mark-monitoring-viewed").catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const data = await apiCall("GET", "/enzoic/monitoring-dashboard");
        setDashboard(data);
      } catch (err) {} finally {
        setLoadingDashboard(false);
      }
    };
    const fetchDomainExposures = async () => {
      try {
        const data = await apiCall("GET", "/enzoic/domain-exposures?pageSize=25");
        setDomainGroups(Array.isArray(data) ? data : []);
      } catch (err) {} finally {
        setLoadingExposures(false);
      }
    };
    fetchDashboard();
    fetchDomainExposures();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!telegramAvailable) return;
    fetchTelegramSignals();
    fetchIncidents();
  }, [telegramAvailable]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        const data = await apiCall("GET", "/enzoic/subscriptions");
        setSubscriptions(data);
      } catch (err) {} finally {
        setLoadingSubscriptions(false);
      }
    };
    fetchSubscriptions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleExpanded = (domain) => {
    setExpandedDomains(prev => ({ ...prev, [domain]: !prev[domain] }));
  };

  const loadMore = useCallback(async (domain) => {
    setLoadingMore(prev => ({ ...prev, [domain]: true }));
    try {
      const currentGroup = domainGroups.find(g => g.domain === domain);
      const nextPageSize = (currentGroup?.details?.length || 0) + 50;
      const data = await apiCall("GET", `/enzoic/domain-exposures?domain=${encodeURIComponent(domain)}&pageSize=${nextPageSize}`);
      const groups = Array.isArray(data) ? data : [];
      if (groups.length > 0)
        setDomainGroups(prev => prev.map(g => g.domain === domain ? groups[0] : g));
    } catch (err) {} finally {
      setLoadingMore(prev => ({ ...prev, [domain]: false }));
    }
  }, [domainGroups, apiCall]);

  const fetchTelegramSignals = useCallback(async (page = 1, severity = "") => {
    setLoadingTelegramSignals(true);
    try {
      const params = new URLSearchParams({ page, pageSize: TELEGRAM_PAGE_SIZE });
      if (severity) params.set("severity", severity);
      const data = await apiCall("GET", `/telegram/signals?${params}`);
      setTelegramSignals(data.items ?? []);
      setTelegramTotal(data.totalCount ?? 0);
      setTelegramPage(page);
      setTelegramLastRefresh(new Date());
    } catch { setTelegramSignals([]); }
    finally { setLoadingTelegramSignals(false); }
  }, [apiCall]);

  const fetchIncidents = useCallback(async (page = 1, issueType = "", severity = "") => {
    setLoadingIncidents(true);
    try {
      const params = new URLSearchParams({ page, pageSize: 20 });
      if (issueType) params.set("issueType", issueType);
      if (severity)  params.set("severity", severity);
      const data = await apiCall("GET", `/telegram/incidents?${params}`);
      setIncidents(data.items ?? []);
      setIncidentTotal(data.totalCount ?? 0);
      setIncidentPage(page);
    } catch { setIncidents([]); }
    finally { setLoadingIncidents(false); }
  }, [apiCall]);


  const totalExposures = useMemo(() =>
    (dashboard?.exposuresByDomain || []).reduce((s, d) => s + d.count, 0),
  [dashboard]);

  const topDomain = useMemo(() => {
    const list = dashboard?.exposuresByDomain || [];
    return list.length > 0 ? [...list].sort((a, b) => b.count - a.count)[0].domain : null;
  }, [dashboard]);

  // Flatten all loaded details for intelligence analysis
  const allDetails = useMemo(() =>
    domainGroups.flatMap(g => g.details || []),
  [domainGroups]);

  // Number of monitored (subscribed) domains: org primary + secondary + deal/BU/vendor domains.
  // This is the real "Monitored Domains" count — distinct from how many domains have exposures.
  const monitoredCount = useMemo(() =>
    (subscriptions?.organization?.primaryDomain ? 1 : 0) +
    (subscriptions?.organization?.secondary?.length || 0) +
    (subscriptions?.deals || []).reduce((sum, d) => sum + (d.domains?.length || 0), 0) +
    (subscriptions?.businessUnits || []).reduce((sum, b) => sum + (b.domains?.length || 0), 0) +
    (subscriptions?.vendors || []).reduce((sum, v) => sum + (v.domains?.length || 0), 0),
  [subscriptions]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">

        {/* Page Header */}
        <div className="mb-6 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center flex-shrink-0">
            <Shield className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Live Monitoring Dashboard</h1>
            <p className="text-sm text-gray-500 mt-0.5">Real-time credential exposure monitoring across all enrolled domains</p>
          </div>
        </div>

        {/* Monitored Domains Banner */}
        <MonitoredDomainsBanner
          subscriptions={subscriptions}
          loading={loadingSubscriptions}
          totalExposures={totalExposures}
        />

        {/* Intelligence Banner + KPI */}
        {!loadingDashboard && (
          <IntelligenceBanner
            totalExposures={totalExposures}
            topDomain={topDomain}
            categoryData={dashboard?.exposuresByCategory}
            timelineData={dashboard?.exposureTimeline}
            uniqueDomains={monitoredCount}
            allDetails={allDetails}
          />
        )}

        {/* Row 1: Charts */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <ExposuresByDomainChart data={dashboard?.exposuresByDomain} loading={loadingDashboard} monitoredCount={monitoredCount} />
          <ExposuresByCategoryChart data={dashboard?.exposuresByCategory} loading={loadingDashboard} />
          <ExposureTimelineChart data={dashboard?.exposureTimeline} loading={loadingDashboard} />
        </div>

        {/* Row 2: Password Security Profile — full width */}
        <div className="mb-6">
          <PasswordSecurityProfile allDetails={allDetails} />
        </div>

        {/* Row 3: Attack Source · Threat Indicators · Why This Matters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <AttackSourceIntelligence allDetails={allDetails} />
          <ThreatIndicators totalExposures={totalExposures} allDetails={allDetails} categoryData={dashboard?.exposuresByCategory} timelineData={dashboard?.exposureTimeline} />
          <WhyThisMattersCard categoryData={dashboard?.exposuresByCategory} totalExposures={totalExposures} />
        </div>

        {/* Row 4: Immediate Actions — full width */}
        <div className="mb-6">
          <ImmediateActionsCard fullWidth allDetails={allDetails} timelineData={dashboard?.exposureTimeline} totalExposures={totalExposures} />
        </div>

        {/* Business Risk Outlook */}
        {!loadingDashboard && <BusinessRiskOutlook totalExposures={totalExposures} categoryData={dashboard?.exposuresByCategory} timelineData={dashboard?.exposureTimeline} />}

        {/* Domain Exposures Table */}
        <DomainExposuresTable
          domainGroups={domainGroups}
          loading={loadingExposures}
          onToggle={toggleExpanded}
          expandedDomains={expandedDomains}
          loadMore={loadMore}
          loadingMore={loadingMore}
        />

        {/* Telegram panels: shown only when telegramMonitoring is available */}
        {telegramAvailable && (<>
        {/* ── Dark-Web Signals ──────────────────────────── */}
        <div className="mt-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 flex-shrink-0">
                <svg className="w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12l-6.871 4.326-2.962-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.833.941z"/>
                </svg>
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">Dark-Web Signals</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {telegramTotal > 0 ? `${telegramTotal} signal${telegramTotal !== 1 ? "s" : ""} captured` : "Live dark-web channel monitoring"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {telegramLastRefresh && (
                <span className="text-xs text-gray-400 hidden sm:block">
                  Updated {telegramLastRefresh.toLocaleTimeString()}
                </span>
              )}
              <button
                onClick={() => fetchTelegramSignals(telegramPage, telegramSeverityFilter)}
                disabled={loadingTelegramSignals}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                title="Refresh signals"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${loadingTelegramSignals ? "animate-spin" : ""}`} />
                Refresh
              </button>
              <select value={telegramSeverityFilter}
                onChange={e => { setTelegramSeverityFilter(e.target.value); fetchTelegramSignals(1, e.target.value); }}
                className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:ring-2 focus:ring-blue-400">
                <option value="">All Severities</option>
                <option>Critical</option><option>High</option><option>Medium</option><option>Low</option>
              </select>
            </div>
          </div>

          {loadingTelegramSignals ? (
            <div className="flex justify-center py-10">
              <span className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : telegramSignals.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
              <p className="text-sm text-gray-400">No signals found. Configure channels and keywords in Platform Configuration.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden divide-y divide-gray-100">
              {telegramSignals.map(sig => {
                const isExpanded = !!expandedSignalIds[sig.telegramSignalId];
                const severityStyle = RISK_COLORS[sig.severity?.toLowerCase()] ?? RISK_COLORS.low;
                const riskDesc = getTelegramRiskDescription(sig);
                const remSteps = getTelegramRemediationSteps(sig);
                return (
                  <div key={sig.telegramSignalId}>
                    <button
                      onClick={() => setExpandedSignalIds(p => ({ ...p, [sig.telegramSignalId]: !p[sig.telegramSignalId] }))}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left transition-colors">
                      {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                      <span className="flex-shrink-0 px-2 py-0.5 rounded text-xs font-semibold"
                        style={{ background: severityStyle.light.replace("bg-", ""), color: severityStyle.hex, backgroundColor: severityStyle.hex + "20" }}>
                        {sig.severity}
                      </span>
                      {sig.channelName?.startsWith("leaksite:") ? (
                        <span className="flex-shrink-0 px-2 py-0.5 bg-red-600 text-white text-xs font-semibold rounded">
                          Ransomware
                        </span>
                      ) : (
                        <span className="font-medium text-sm text-gray-900 flex-shrink-0">@{sig.channelName}</span>
                      )}
                      {sig.ioCs?.length > 0 && (
                        <span className="flex-shrink-0 px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded-full border border-purple-200">
                          {sig.ioCs.length} IoC{sig.ioCs.length !== 1 ? "s" : ""}
                        </span>
                      )}
                      {sig.matchedKeywords?.slice(0, 3).map(kw => (
                        <span key={kw} className="flex-shrink-0 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full border border-blue-200">{kw}</span>
                      ))}
                      {sig.matchedKeywords?.length > 3 && <span className="text-xs text-gray-400">+{sig.matchedKeywords.length - 3}</span>}
                      <span className="flex-1 min-w-0 text-xs text-gray-500 truncate ml-2">{sig.snippet}</span>
                      <span className="flex-shrink-0 text-xs text-gray-400 ml-2">{formatRelativeTime(sig.messageTimestamp)}</span>
                    </button>

                    {isExpanded && (
                      <div className="border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-100">
                        <div className="px-5 py-4 space-y-3 bg-gray-50">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Signal Intelligence</p>
                          <div className="bg-white rounded-lg p-3 text-xs text-gray-700 border border-gray-200 font-mono leading-relaxed whitespace-pre-wrap break-words">
                            {sig.snippet}
                          </div>
                          {sig.matchedKeywords?.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-gray-500 mb-1">Matched Keywords</p>
                              <div className="flex flex-wrap gap-1.5">
                                {sig.matchedKeywords.map(kw => (
                                  <span key={kw} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded border border-blue-200">{kw}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          {sig.ioCs?.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-gray-500 mb-1">Indicators of Compromise ({sig.ioCs.length})</p>
                              <div className="space-y-1">
                                {sig.ioCs.map((ioc, i) => (
                                  <div key={i} className="flex items-center justify-between bg-white rounded px-3 py-1.5 border border-gray-200">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <span className="text-xs font-mono font-medium text-gray-400 uppercase flex-shrink-0">{ioc.type}</span>
                                      <span className="text-xs font-mono text-gray-700 truncate">{ioc.value}</span>
                                    </div>
                                    <button
                                      onClick={() => { navigator.clipboard.writeText(ioc.value); setCopiedIoC(`${sig.telegramSignalId}-${i}`); setTimeout(() => setCopiedIoC(null), 1500); }}
                                      className="ml-2 flex-shrink-0 text-gray-400 hover:text-blue-600 transition-colors">
                                      {copiedIoC === `${sig.telegramSignalId}-${i}` ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {sig.messageLink && (
                            <a href={sig.messageLink} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700">
                              <ExternalLink className="w-3.5 h-3.5" /> Open in Telegram
                            </a>
                          )}
                          <p className="text-xs text-gray-400">
                            Score: <span className="font-semibold" style={{ color: severityStyle.hex }}>{sig.severityScore}/100</span>
                            {" · "}Captured {new Date(sig.createdAt).toLocaleString()}
                          </p>
                        </div>

                        <div className="px-5 py-4 space-y-3">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Risk & Remediation</p>
                          <div className="p-3 rounded-lg border-l-4 text-xs"
                            style={{ borderLeftColor: severityStyle.hex, backgroundColor: severityStyle.hex + "15" }}>
                            <p className="font-semibold text-gray-800 mb-1">{sig.severity} Risk</p>
                            <p className="text-gray-600 leading-relaxed">{riskDesc}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-700 mb-2">Recommended Actions</p>
                            <ol className="space-y-1.5">
                              {remSteps.map((step, i) => (
                                <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
                                  <span className="flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center text-white text-[10px] font-bold mt-0.5"
                                    style={{ background: severityStyle.hex }}>{i + 1}</span>
                                  {step}
                                </li>
                              ))}
                            </ol>
                          </div>
                          <div className="pt-2 border-t border-gray-100">
                            <p className="text-xs font-medium text-gray-500 mb-1">Compliance</p>
                            <div className="flex flex-wrap gap-1.5">
                              {["Public channel", "No PII stored", "ToS compliant", "No history backfill"].map(label => (
                                <span key={label} className="px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded-full border border-green-200">✓ {label}</span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {telegramTotal > TELEGRAM_PAGE_SIZE && (
            <div className="flex items-center justify-center gap-3 mt-4">
              <button onClick={() => fetchTelegramSignals(telegramPage - 1, telegramSeverityFilter)} disabled={telegramPage <= 1}
                className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 disabled:opacity-40">
                Previous
              </button>
              <span className="text-xs text-gray-500">Page {telegramPage} of {Math.ceil(telegramTotal / TELEGRAM_PAGE_SIZE)}</span>
              <button onClick={() => fetchTelegramSignals(telegramPage + 1, telegramSeverityFilter)} disabled={telegramPage >= Math.ceil(telegramTotal / TELEGRAM_PAGE_SIZE)}
                className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 disabled:opacity-40">
                Next
              </button>
            </div>
          )}
        </div>

        {/* ── Threat Incidents ──────────────────────────── */}
        <div className="mt-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-red-100 flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">Threat Incidents</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {incidentTotal > 0 ? `${incidentTotal} incident${incidentTotal !== 1 ? "s" : ""} generated` : "Correlation engine incident feed"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select value={incidentIssueTypeFilter}
                onChange={e => { setIncidentIssueTypeFilter(e.target.value); fetchIncidents(1, e.target.value, incidentSeverityFilter); }}
                className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:ring-2 focus:ring-red-400">
                <option value="">All Types</option>
                <option value="credential_leak">Credential Leak</option>
                <option value="ransomware_naming">Ransomware Naming</option>
                <option value="iab_listing">IAB Listing</option>
                <option value="phishing_infra">Phishing Infra</option>
                <option value="cve_exploit_active">CVE Exploit</option>
              </select>
              <select value={incidentSeverityFilter}
                onChange={e => { setIncidentSeverityFilter(e.target.value); fetchIncidents(1, incidentIssueTypeFilter, e.target.value); }}
                className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:ring-2 focus:ring-red-400">
                <option value="">All Severities</option>
                <option>Critical</option><option>High</option><option>Medium</option><option>Low</option>
              </select>
            </div>
          </div>

          {loadingIncidents ? (
            <div className="flex justify-center py-10">
              <span className="w-6 h-6 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : incidents.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
              <p className="text-sm text-gray-400">No incidents yet. Incidents are created automatically when signals correlate against your organization profile.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden divide-y divide-gray-100">
              {incidents.map(inc => {
                const isExpanded = !!expandedIncidentIds[inc.telegramIncidentId];
                const sevColor = RISK_COLORS[inc.severity?.toLowerCase()] ?? RISK_COLORS.low;
                const issueTypeLabel = {
                  credential_leak: "Credential Leak", ransomware_naming: "Ransomware", iab_listing: "IAB",
                  phishing_infra: "Phishing", cve_exploit_active: "CVE Exploit"
                }[inc.issueType] ?? inc.issueType;
                const slaExpired = inc.slaDueUtc && new Date(inc.slaDueUtc) < new Date();
                return (
                  <div key={inc.telegramIncidentId}>
                    <button
                      onClick={() => setExpandedIncidentIds(p => ({ ...p, [inc.telegramIncidentId]: !p[inc.telegramIncidentId] }))}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left transition-colors">
                      {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                      <span className="flex-shrink-0 px-2 py-0.5 rounded text-xs font-semibold"
                        style={{ backgroundColor: sevColor.hex + "20", color: sevColor.hex }}>
                        {inc.severity}
                      </span>
                      <span className="flex-shrink-0 px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded font-medium">{issueTypeLabel}</span>
                      <span className="flex-1 min-w-0 text-sm font-medium text-gray-900 truncate">{inc.title}</span>
                      <span className="flex-shrink-0 text-xs font-semibold text-gray-500">Score {inc.score}</span>
                      {inc.slaLevel && (
                        <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded font-medium ${slaExpired ? "bg-red-100 text-red-700" : "bg-blue-50 text-blue-700"}`}>
                          {inc.slaLevel}{slaExpired ? " BREACHED" : ""}
                        </span>
                      )}
                      <span className="flex-shrink-0 text-xs text-gray-400 ml-2">{formatRelativeTime(inc.createdAt)}</span>
                    </button>

                    {isExpanded && (
                      <div className="border-t border-gray-100 bg-gray-50 px-5 py-4 space-y-4">
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Narrative</p>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{inc.narrative}</p>
                        </div>
                        {inc.affectedEmails?.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Affected Emails</p>
                            <div className="flex flex-wrap gap-1">
                              {inc.affectedEmails.map(e => (
                                <span key={e} className="px-2 py-0.5 bg-red-50 text-red-700 text-xs rounded border border-red-200">{e}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {inc.affectedAssets?.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Affected Assets</p>
                            <div className="flex flex-wrap gap-1">
                              {inc.affectedAssets.map(a => (
                                <span key={a} className="px-2 py-0.5 bg-orange-50 text-orange-700 text-xs rounded border border-orange-200">{a}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {inc.recommendedActions?.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Recommended Actions</p>
                            <ul className="space-y-1">
                              {inc.recommendedActions.map((a, i) => (
                                <li key={i} className="flex items-start gap-2 text-xs text-gray-700">
                                  <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                                  {a}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {inc.slaDueUtc && (
                          <p className="text-xs text-gray-500">
                            SLA due: <span className={slaExpired ? "text-red-600 font-semibold" : "text-gray-700"}>{new Date(inc.slaDueUtc).toLocaleString()}</span>
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {incidentTotal > 20 && (
            <div className="flex items-center justify-center gap-4 mt-3">
              <button onClick={() => fetchIncidents(incidentPage - 1, incidentIssueTypeFilter, incidentSeverityFilter)} disabled={incidentPage <= 1}
                className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 disabled:opacity-40">Previous</button>
              <span className="text-xs text-gray-500">Page {incidentPage} of {Math.ceil(incidentTotal / 20)}</span>
              <button onClick={() => fetchIncidents(incidentPage + 1, incidentIssueTypeFilter, incidentSeverityFilter)} disabled={incidentPage >= Math.ceil(incidentTotal / 20)}
                className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 disabled:opacity-40">Next</button>
            </div>
          )}
        </div>
        </>)}

        {/* Footer */}
        <div className="mt-8 text-center border-t border-gray-200 pt-6">
          <p className="text-xs text-gray-400">
            <span className="font-semibold text-gray-500">Axaris Intelligence</span>
            {" "}·{" "}Predict. Resolve. Verify.
          </p>
        </div>

      </div>
    </div>
  );
};

export default DarkWebLiveMonitoringDashboard;
